<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import Link from "../../../shared/components/Link.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import type { ServicesProps } from "./ServicesProps.js";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import { servicesPathMap } from "../../../../core/schema/paths/servicesPathMap.js";
	import { Service } from "../../../../core/schema/Service.js";
	import integrationXs from "../assets/images/services/integration.png?webp&w=400&h=256&imagetools";
	import integrationSm from "../assets/images/services/integration.png?webp&w=640&h=410&imagetools";
	import integrationMd from "../assets/images/services/integration.png?webp&w=768&h=410&imagetools";
	import integrationLg from "../assets/images/services/integration.png?webp&w=500&h=520&imagetools";
	import integrationXl from "../assets/images/services/integration.png?webp&w=630&h=580&imagetools";
	import consultationXs from "../assets/images/services/consultation.png?webp&w=400&h=256&imagetools";
	import consultationSm from "../assets/images/services/consultation.png?webp&w=640&h=410&imagetools";
	import consultationMd from "../assets/images/services/consultation.png?webp&w=768&h=410&imagetools";
	import consultationLg from "../assets/images/services/consultation.png?webp&w=500&h=520&imagetools";
	import consultationXl from "../assets/images/services/consultation.png?webp&w=630&h=580&imagetools";
	import serviceXs from "../assets/images/services/service.png?webp&w=400&h=256&imagetools";
	import serviceSm from "../assets/images/services/service.png?webp&w=640&h=410&imagetools";
	import serviceMd from "../assets/images/services/service.png?webp&w=768&h=410&imagetools";
	import serviceLg from "../assets/images/services/service.png?webp&w=500&h=520&imagetools";
	import serviceXl from "../assets/images/services/service.png?webp&w=630&h=580&imagetools";
	import financingXs from "../assets/images/services/financing.png?webp&w=400&h=256&imagetools";
	import financingSm from "../assets/images/services/financing.png?webp&w=640&h=410&imagetools";
	import financingMd from "../assets/images/services/financing.png?webp&w=768&h=410&imagetools";
	import financingLg from "../assets/images/services/financing.png?webp&w=500&h=520&imagetools";
	import financingXl from "../assets/images/services/financing.png?webp&w=630&h=580&imagetools";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import type { SourceSetQuery } from "../../../shared/components/Picture/SourceSetQuery.js";

	type $$Props = ServicesProps;

	const { TENANT_LOCALE } = getGlobalSettings();
	const { serviceTexts } = $$restProps as $$Props;

	const imageMap = {
		[Service.Integration]: {
			"<sm": integrationXs,
			"<md": integrationSm,
			">md": integrationMd,
			">lg": integrationLg,
			">xl": integrationXl,
		},
		[Service.Consultation]: {
			"<sm": consultationXs,
			"<md": consultationSm,
			">md": consultationMd,
			">lg": consultationLg,
			">xl": consultationXl,
		},
		[Service.Service]: {
			"<sm": serviceXs,
			"<md": serviceSm,
			">md": serviceMd,
			">lg": serviceLg,
			">xl": serviceXl,
		},
		[Service.Financing]: {
			"<sm": financingXs,
			"<md": financingSm,
			">md": financingMd,
			">lg": financingLg,
			">xl": financingXl,
		},
	} satisfies Record<Service, Partial<Record<SourceSetQuery, string>>>;
</script>

<Layout>
	<div class="container">
		{#each Object.values(Service) as service, index}
			{@const texts = serviceTexts[service]}
			<section
				class="{index > 0 ? '!mt-12' : 'mt-0'} flex flex-col gap-5 lg:gap-8 lg:p-8 xl:flex-row"
				id={service}
				class:xl:!flex-row-reverse={index % 2 === 0}
			>
				<div class="xl:auto h-min max-h-[25.5rem] overflow-hidden rounded-lg bg-gray-300 lg:max-h-none lg:w-1/2 xl:p-4">
					<StaticPicture
						image={imageMap[service][">xl"]}
						alt={texts.headline[TENANT_LOCALE]}
						width={620}
						height={520}
						loading={index <= 1 ? "eager" : "lazy"}
						fetchpriority={index === 0 ? "high" : undefined}
						preload={index === 0 ? true : undefined}
						decoding={index > 1 ? "async" : undefined}
						imgClass="rounded w-full xl:w-auto h-auto object-cover"
						class="h-full w-full overflow-hidden rounded lg:max-h-none"
						sourcesets={imageMap[service]}
					/>
				</div>
				<div
					class="flex max-w-[50rem] flex-col items-start justify-center xl:h-[34.5rem] xl:w-1/2 xl:pl-8 xl:pr-[7rem]"
				>
					{#if index === 0}
						<h1 class="m-0 mb-4 p-0 text-lg font-normal lg:mb-8 xl:text-xl">
							{nbspify(texts.headline[TENANT_LOCALE])}
						</h1>
					{:else}
						<h2 class="m-0 mb-4 p-0 font-normal lg:mb-8 xl:text-xl">{nbspify(texts.headline[TENANT_LOCALE])}</h2>
					{/if}
					{#each texts.paragraphs[TENANT_LOCALE] as paragraph, index}
						{#if "heading" in paragraph && paragraph.heading}
							<span class="text-xs font-semibold">{nbspify(paragraph.heading)}</span>
						{/if}
						<p
							class="text-xs leading-5 lg:mb-5"
							class:xl:!mb-16={texts.paragraphs[TENANT_LOCALE].length - 1 === index}
							class:lg:!mb-8={texts.paragraphs[TENANT_LOCALE].length - 1 === index}
						>
							{nbspify(paragraph.text)}
						</p>
					{/each}
					<div class="flex w-full flex-col gap-2 lg:w-[110%] lg:flex-row">
						{#if texts.primaryButton}
							<Link
								asButton
								variant="primary"
								icon={arrowRightLong}
								href="{servicesPathMap[TENANT_LOCALE]}/{texts.primaryButton.slug[TENANT_LOCALE]}"
								class="justify-between lg:justify-normal"
							>
								{nbspify(texts.primaryButton.text[TENANT_LOCALE])}
							</Link>
						{/if}
						{#if texts.secondaryButton}
							<Link
								asButton
								variant="secondary"
								icon={arrowRightLong}
								href="/{texts.secondaryButton.slug[TENANT_LOCALE]}"
								class="justify-between lg:justify-normal"
							>
								{nbspify(texts.secondaryButton.text[TENANT_LOCALE])}
							</Link>
						{/if}
						{#if texts.externalLink}
							<Link
								asButton
								variant="secondary"
								icon={arrowRightLong}
								href={texts.externalLink.slug[TENANT_LOCALE]}
								class="justify-between lg:justify-normal"
								target="_blank"
							>
								{nbspify(texts.externalLink.text[TENANT_LOCALE])}
							</Link>
						{/if}
					</div>
				</div>
			</section>
		{/each}
	</div>
</Layout>
